import { useCallback, useEffect, useRef, useState } from 'react'
import constant from 'lodash/constant'
import isError from 'lodash/isError'

import type { AgentChatMessage, AgentFrame } from '@klear/schema/agent-console'
import { ROUTE_API_AGENT } from '@klear/constants'
import { getApiOrigin } from '@klear/runtime-env'
import { useCurrentAgentContext } from '@klear/ui/contexts/agent-context'

export interface AgentToolRun {
  id: string
  name: string
  status: 'running' | 'ok' | 'error'
}

export interface AgentTurn {
  role: 'user' | 'assistant'
  text: string
  tools: AgentToolRun[]
  status: 'streaming' | 'done' | 'error'
  error?: string
}

export function useAgentChat(traderId?: number) {
  const context = useCurrentAgentContext()
  const [turns, setTurns] = useState<AgentTurn[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const sessionIdRef = useRef<string | null>(null)

  useEffect(() => () => abortRef.current?.abort(), [])

  const updateLast = useCallback((mutate: (turn: AgentTurn) => AgentTurn) => {
    setTurns((current) => {
      if (current.length === 0) return current
      const last = current[current.length - 1]
      if (last == null) return current
      return [...current.slice(0, -1), mutate(last)]
    })
  }, [])

  const applyFrame = useCallback(
    (frame: AgentFrame) => {
      switch (frame.type) {
        case 'session':
          sessionIdRef.current = frame.id
          return
        case 'text':
          updateLast((turn) => ({ ...turn, text: turn.text + frame.text }))
          return
        case 'tool':
          updateLast((turn) => {
            const existing = turn.tools.findIndex((tool) => tool.id === frame.id)
            const run: AgentToolRun = {
              id: frame.id,
              name: frame.name,
              status: frame.status
            }
            if (existing < 0) return { ...turn, tools: [...turn.tools, run] }
            return { ...turn, tools: turn.tools.map((tool, index) => (index === existing ? run : tool)) }
          })
          return
        case 'reset':
          updateLast((turn) => ({ ...turn, text: '' }))
          return
        case 'done':
          updateLast((turn) => ({ ...turn, status: 'done' }))
          return
        case 'error':
          updateLast((turn) => ({ ...turn, status: 'error', error: frame.message }))
          return
      }
    },
    [updateLast]
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setIsStreaming(false)
    updateLast((turn) => (turn.status === 'streaming' ? { ...turn, status: 'done' } : turn))
  }, [updateLast])

  const send = useCallback(
    (message: string): boolean => {
      const trimmed = message.trim()
      if (trimmed.length === 0 || abortRef.current != null) return false

      const history: AgentChatMessage[] = turns
        .filter((turn) => turn.status !== 'error' && turn.text.trim().length > 0)
        .map((turn) => ({ role: turn.role, text: turn.text }))

      const controller = new AbortController()
      abortRef.current = controller

      setTurns((current) => [
        ...current,
        { role: 'user', text: trimmed, tools: [], status: 'done' },
        { role: 'assistant', text: '', tools: [], status: 'streaming' }
      ])
      setIsStreaming(true)

      void (async () => {
        let terminalFrameReceived = false
        const applyLine = (line: string) => {
          if (line.trim().length === 0) return
          const frame = JSON.parse(line) as AgentFrame
          terminalFrameReceived ||= frame.type === 'done' || frame.type === 'error'
          applyFrame(frame)
        }

        try {
          const response = await fetch(`${getApiOrigin()}${ROUTE_API_AGENT}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              history,
              message: trimmed,
              sessionId: sessionIdRef.current ?? undefined,
              traderId,
              ...(context !== null && { context })
            }),
            signal: controller.signal
          })

          if (!response.ok || response.body == null) {
            const detail = await response.text().catch(constant(''))
            throw new Error(detail.length > 0 ? detail : `Agent request failed (${response.status}).`)
          }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''
            for (const line of lines) applyLine(line)
          }

          buffer += decoder.decode()
          applyLine(buffer)
          if (!terminalFrameReceived) throw new Error('The agent response ended unexpectedly.')
        } catch (error) {
          if (controller.signal.aborted) return
          updateLast((turn) => ({
            ...turn,
            status: 'error',
            error: isError(error) ? error.message : 'The agent could not be reached.'
          }))
        } finally {
          if (abortRef.current === controller) {
            abortRef.current = null
            setIsStreaming(false)
          }
        }
      })()

      return true
    },
    [applyFrame, context, traderId, turns, updateLast]
  )

  const clear = useCallback(() => {
    stop()
    sessionIdRef.current = null
    setTurns([])
  }, [stop])

  return { turns, isStreaming, send, stop, clear }
}
